type MemoryPerformance = Performance & {
  memory?: {
    jsHeapSizeLimit: number
    totalJSHeapSize: number
    usedJSHeapSize: number
  }
}

interface PerformanceMonitorOptions {
  intervalMs?: number
}

interface PerformanceSnapshot {
  audioElements: number
  domNodes: number
  imageElements: number
  jsHeapUsedMb: number | null
  longTasks: number
  playingVideos: number
  videoElements: number
}

const DOM_NODE_WARN_LIMIT = 3500
const MEDIA_ELEMENT_WARN_LIMIT = 12
const PLAYING_VIDEO_WARN_LIMIT = 2
const JS_HEAP_WARN_LIMIT_MB = 420
const LONG_TASK_WARN_LIMIT = 6
const DEFAULT_INTERVAL_MS = 5000
const WARN_COOLDOWN_MS = 30000

let started = false
let longTaskCount = 0
let lastWarnAt = 0
let lastWarnSignature = ''
let observer: PerformanceObserver | null = null

function shouldStartMonitor() {
  if (typeof window === 'undefined' || started) {
    return false
  }

  if (import.meta.env.DEV) {
    return true
  }

  return new URLSearchParams(window.location.search).has('dreama_perf')
}

function startLongTaskObserver() {
  if (typeof PerformanceObserver === 'undefined' || observer) {
    return
  }

  try {
    observer = new PerformanceObserver((entries) => {
      longTaskCount += entries.getEntries().length
    })
    observer.observe({ entryTypes: ['longtask'] })
  } catch {
    observer = null
  }
}

function collectPerformanceSnapshot(): PerformanceSnapshot {
  const videos = Array.from(document.querySelectorAll('video'))
  const memory = performance as MemoryPerformance
  const jsHeapUsedMb =
    typeof memory.memory?.usedJSHeapSize === 'number'
      ? Math.round(memory.memory.usedJSHeapSize / 1024 / 1024)
      : null

  return {
    audioElements: document.querySelectorAll('audio').length,
    domNodes: document.getElementsByTagName('*').length,
    imageElements: document.querySelectorAll('img').length,
    jsHeapUsedMb,
    longTasks: longTaskCount,
    playingVideos: videos.filter((video) => !video.paused && !video.ended).length,
    videoElements: videos.length,
  }
}

function collectBudgetIssues(snapshot: PerformanceSnapshot) {
  const issues: string[] = []

  if (snapshot.domNodes > DOM_NODE_WARN_LIMIT) {
    issues.push('domNodes')
  }

  if (snapshot.videoElements + snapshot.audioElements > MEDIA_ELEMENT_WARN_LIMIT) {
    issues.push('mediaElements')
  }

  if (snapshot.playingVideos > PLAYING_VIDEO_WARN_LIMIT) {
    issues.push('playingVideos')
  }

  if (snapshot.longTasks > LONG_TASK_WARN_LIMIT) {
    issues.push('longTasks')
  }

  if (snapshot.jsHeapUsedMb !== null && snapshot.jsHeapUsedMb > JS_HEAP_WARN_LIMIT_MB) {
    issues.push('jsHeap')
  }

  return issues
}

function shouldWarnBudget(issues: string[]) {
  if (!issues.length) {
    lastWarnSignature = ''
    return false
  }

  const now = Date.now()
  const signature = issues.join('|')

  if (signature === lastWarnSignature && now - lastWarnAt < WARN_COOLDOWN_MS) {
    return false
  }

  lastWarnAt = now
  lastWarnSignature = signature

  return true
}

export function startDreamaPerformanceMonitor(options: PerformanceMonitorOptions = {}) {
  if (!shouldStartMonitor()) {
    return
  }

  started = true
  startLongTaskObserver()

  window.setInterval(() => {
    const snapshot = collectPerformanceSnapshot()

    ;(window as typeof window & { __DREAMA_PERF__?: PerformanceSnapshot }).__DREAMA_PERF__ =
      snapshot

    const issues = collectBudgetIssues(snapshot)

    if (shouldWarnBudget(issues)) {
      console.warn('[Dreama perf budget]', {
        issues,
        snapshot,
      })
    }

    longTaskCount = 0
  }, options.intervalMs ?? DEFAULT_INTERVAL_MS)
}
